'use client';

import FlightUpIcon from '@/assets/icon/flight-up';
import BookingSummary from '@/components/booking-summary/summary';
import FareSummary from '@/components/fare-summary/fare-summary';
import { Root } from '@/components/list/flightlist/types';
import {
  useAddToCartMutation,
  useCreateUserMutation,
  useGetOrderDetailsMutation,
  useVerifyRazorpayPaymentMutation,
  useGenerateSecurePaymentTokenMutation,
  useGetAtomScriptUrlMutation,
} from '@flysync/shared-redux/services/booking';
import { Clock } from 'lucide-react';
import Image from '@/lib/app-image';
import React, { useEffect, useState } from 'react';
import { useRouter } from '@/lib/navigation';
import { useSelector } from 'react-redux';
import {
  updateInitiateReqData,
  updateFareDetails,
} from '@flysync/shared-redux/slices/flightSearchSlice';
import { useDispatch } from 'react-redux';
import { cn, formatCurrency } from '@/lib/utils';
import FlightDownIcon from '@/assets/icon/flight-down';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { getAirlineIconUrl } from '@/lib/airlineIcon';
import { config } from '@/config';
import CryptoJS from 'crypto-js';

// Type definitions for Atom payment
interface AtomPaymentDetails {
  tokenId: string;
  merchantId: string;
  custEmail: string;
  custMobile: string;
  returnUrl: string;
}

// Extend Window interface for Atom payment
declare global {
  interface Window {
    AtomPaynetz: any;
  }
}

// Client-side encryption and signature generation
// Secret key should be provided through the app environment configuration.

const SECRET_KEY = config.encryptionSecretKey;

/**
 * Encrypt data using AES encryption
 * @param plainText - Data to encrypt
 * @returns Encrypted string
 */
const encryptData = (plainText: string | number): string => {
  try {
    if (!SECRET_KEY) {
      console.error('ENCRYPTION_SECRET_KEY is not set');
      return typeof plainText === 'number'
        ? plainText.toString()
        : String(plainText);
    }

    const text =
      typeof plainText === 'number' ? plainText.toString() : plainText;
    const encrypted = CryptoJS.AES.encrypt(text, SECRET_KEY).toString();
    return encrypted;
  } catch (error) {
    console.error('Encryption error:', error);
    return typeof plainText === 'number'
      ? plainText.toString()
      : String(plainText);
  }
};

/**
 * Generate HMAC signature
 * @param payload - Payload string to sign
 * @returns HMAC signature
 */
const generateSignature = (payload: string): string => {
  try {
    if (!SECRET_KEY) {
      console.error('ENCRYPTION_SECRET_KEY is not set');
      throw new Error('Secret key not configured');
    }

    const signature = CryptoJS.HmacSHA256(payload, SECRET_KEY).toString();
    return signature;
  } catch (error) {
    console.error('Signature generation error:', error);
    throw error;
  }
};

const Review = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const userInput = useSelector((state: any) => state.userInputs);
  const { selectedFlight, selectedReturnFlight, selectedBucket } = userInput;

  const [addToCart] = useAddToCartMutation();

  const [createUser] = useCreateUserMutation();

  const [getOrderDetails] = useGetOrderDetailsMutation();

  const [verifyRazorpay] = useVerifyRazorpayPaymentMutation();

  const [generateSecurePaymentToken] = useGenerateSecurePaymentTokenMutation();

  const [getAtomScriptUrlService] = useGetAtomScriptUrlMutation();

  const flightSearch = useSelector(
    (state: { flightSearch: any }) => state.flightSearch,
  );

  const { toast } = useToast();

  const [isSelectedReturnFlight, setSelectedReturnFlight] =
    useState(selectedReturnFlight);
  useEffect(() => {
    // Clear return flight data if trip type is not RT (Round Trip)
    if (flightSearch?.travelType !== 'RT') {
      setSelectedReturnFlight([]);
    }
  }, [flightSearch?.tripType]);

  const callback = async () => {
    if (sessionStorage.getItem('token')) {
      await handleAddToCart();
    } else {
      await handleCreateUser();
    }
  };

  const handleCreateUser = async () => {
    try {
      const data = {
        email: flightSearch.email_id,
        mobile: flightSearch.mobile_no,
      };
      const res = await createUser({
        data,
      }).unwrap();
      let { code, message } = res.status;
      if (code == 200) {
        sessionStorage.setItem('token', res.status.data.token);
        sessionStorage.setItem('user', JSON.stringify(res.status.data.data));
        await handleAddToCart();
        toast({
          title: 'Success',
          description: message,
          variant: 'success',
        });
        return;
      }
      sessionStorage.setItem('tempEmail', flightSearch.email_id);
      document.getElementById('signInButton')?.click();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleAddToCart = async () => {
    const addToCartData: any = await addToCartCall();
    if (addToCartData.data.booking_id) {
      if (addToCartData.data.fareIncrease) {
        const fareDetails = {
          ...flightSearch.fareDetails,
          netfare: addToCartData.data.fare_details.netfare,
        };
        dispatch(updateFareDetails(fareDetails));
      }
      let reqData = {
        ...flightSearch,
        passenger: flightSearch.passenger.map((data: any) => ({
          ...data,
          save_passenger: 'Yes',
        })),
        ...flightSearch.passengers,
      };
      reqData.data = reqData.data.map((item: any, index: number) => ({
        ...item,
        price_details: {
          ...item.price_details,
          otherInfo:
            addToCartData.data.otherInfo.length > 0
              ? addToCartData.data.otherInfo[index]
              : item.price_details.otherInfo,
        },
      }));
      let data = {
        ...reqData,
        booking_id: addToCartData.data.booking_id,
        amount: Number(
          flightSearch?.fareDetails?.netfare +
            (flightSearch?.fareDetails?.fareDetails?.seat || 0) +
            (flightSearch?.fareDetails?.meal || 0) +
            (flightSearch?.fareDetails?.baggage || 0),
        ).toFixed(2),
        isholdPnr: false,
        totalSSR: [
          {
            label: 'Baggage',
            value: Number(flightSearch?.fareDetails?.baggage || 0),
          },
          {
            label: 'Seat',
            value: Number(flightSearch?.fareDetails?.fareDetails?.seat || 0),
          },
          {
            label: 'Meal',
            value: Number(flightSearch?.fareDetails?.meal || 0),
          },
        ],
        total_ssr_amount: Number(
          (flightSearch?.fareDetails?.fareDetails?.seat || 0) +
            (flightSearch?.fareDetails?.meal || 0) +
            (flightSearch?.fareDetails?.baggage || 0),
        ).toFixed(2),
        agency_markup: 0,
        payment_mode: 'PG',
      };

      await apiCall(data);
    }
  };

  const addToCartCall = async () => {
    const { fareRules, fare_details, priceIds, trip_type } = flightSearch;
    const flightData = flightSearch.data.map((item: any) => {
      const contactDetails = {
        mobile: flightSearch.mobile_no,
        email: flightSearch.email_id,
      };
      return {
        ...item,
        gst: flightSearch.gst,
        contact_details: contactDetails,
      };
    });
    const res = await addToCart({
      data: {
        data: flightData,
        fareRules,
        fare_details,
        priceIds,
        booking_id: flightSearch.cartId,
        passenger: flightSearch.passenger,
        flowType: 'review',
      },
      auth: sessionStorage.getItem('token') || '',
    });
    if (res?.data?.code === 403) {
      toast({
        title: 'Error',
        description: res?.data?.message,
        variant: 'error',
      });
      router.push('/flight-list');
    }
    return res.data;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const verifyRazorpayPaymentData = async (response: any, receipt: string) => {
    let reqData = response;
    reqData.orderId = receipt;
    const res = await verifyRazorpay({ data: reqData }).unwrap();
    if (res?.code == 200 && res?.data?.status.toLowerCase() == 'success') {
      location.href = res?.data?.redirectUrl;
    } else {
      toast({
        title: 'Error',
        description: 'Payment failed',
        variant: 'error',
      });
    }
  };

  const apiCall = async (data: any) => {
    dispatch(updateInitiateReqData(data));

    const userInfo = JSON.parse(sessionStorage.getItem('user') || '{}');
    const authToken = sessionStorage.getItem('token') || '';
    const totalAmount = Number(
      flightSearch?.fareDetails?.netfare +
        (flightSearch?.fareDetails?.fareDetails?.seat || 0) +
        (flightSearch?.fareDetails?.meal || 0) +
        (flightSearch?.fareDetails?.baggage || 0),
    ).toFixed(2);

    try {
      // Encrypt the amount
      const encryptedAmount = encryptData(totalAmount);

      const trackId = Date.now().toString();

      // Create payload for signature
      const payloadSign = `${Number(totalAmount).toFixed(2)}:${trackId}:${userInfo.user_id}`;

      console.log(payloadSign, 'payloadSign');

      // Generate signature
      const signature = generateSignature(payloadSign);

      const orderDetails = {
        ...data,
        amount: encryptedAmount, // Send encrypted amount
        signature,
        user_info: {
          user_id: userInfo.user_id || userInfo.agencyId,
          email: userInfo.email,
          phone: userInfo.phone || '9999999999',
          agency_name: userInfo.agency_name || 'Customer',
          agent_id: userInfo.agent_id || 'GUEST',
        },
        trackId, // Generate or get from context
        type: 'booking',
        auth: authToken,
        product_type: 'B2C',
        booking_id: data.booking_id,
        payment_mode: 'PG',
      };
      const securePaymentToken = await generateSecurePaymentToken({
        data: orderDetails,
        auth: authToken,
      }).unwrap();

      const getOrderDetailsRes = await getOrderDetails({
        data: {
          ...orderDetails,
          order_id: orderDetails.trackId,
          securePaymentToken: securePaymentToken?.data?.securePaymentToken,
        },
        auth: sessionStorage.getItem('token') || '',
      }).unwrap();

      sessionStorage.setItem('mailToCustomer', 'true');

      // data.booking_id = initRes?.data || '';
      if (getOrderDetailsRes?.data) {
        // dispatch(updateField({ field: 'booking_id', value: data.booking_id }));
        if (getOrderDetailsRes?.data.id == 'PH') {
          location.assign(getOrderDetailsRes?.data?.redirectUrl);
        }

        if (getOrderDetailsRes?.data.id == 'RP') {
          const razorpayScriptLoaded = await loadRazorpayScript();
          if (razorpayScriptLoaded) {
            const options = {
              key: getOrderDetailsRes?.data?.razorpayKey,
              amount: getOrderDetailsRes?.data?.amount.toString(),
              currency: 'INR',
              description: getOrderDetailsRes?.data?.receipt,
              order_id: getOrderDetailsRes?.data?.razorpayId,
              name: config.corporateName,
              method: {
                emi: false,
              },
              handler: (response: any) => {
                verifyRazorpayPaymentData(
                  response,
                  getOrderDetailsRes?.data?.receipt,
                );
              },
              prefill: {
                name:
                  JSON.parse(sessionStorage.getItem('user') || '{}')
                    ?.user_name ||
                  JSON.parse(sessionStorage.getItem('user') || '{}')?.email,
                email: JSON.parse(sessionStorage.getItem('user') || '{}')
                  ?.email,
                contact: JSON.parse(sessionStorage.getItem('user') || '{}')
                  ?.mobile,
              },
              theme: {
                color: '#F37254',
              },
              modal: {
                ondismiss: () => {
                  toast({
                    title: 'Error',
                    description: 'Payment failed',
                    variant: 'error',
                  });
                },
              },
            };

            const rzp = new (window as any).Razorpay(options);
            rzp.on('payment.failed', (response: any) => {
              console.error('Payment failed:', response.error);
              toast({
                title: 'Error',
                description: 'Payment failed',
                variant: 'error',
              });
            });
            rzp.open();
          }
        }

        if (getOrderDetailsRes?.data.id == 'HDFC') {
          location.assign(getOrderDetailsRes?.data?.paymentUrl);
        }

        if (getOrderDetailsRes?.data.id == 'EB') {
          location.assign(getOrderDetailsRes?.data?.paymentUrl);
        }

        if (getOrderDetailsRes?.data.id == 'ATOM') {
          handleAtomPayment({
            tokenId: getOrderDetailsRes?.data.tokenId,
            merchantId: getOrderDetailsRes?.data.merchantId,
            custEmail: getOrderDetailsRes?.data?.customer_email || '',
            custMobile: getOrderDetailsRes?.data?.customer_phone || '',
            returnUrl: getOrderDetailsRes?.data.redirectUrl,
          });
        }
      } else {
        toast({
          title: 'Error',
          description: 'Booking failure',
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Payment processing failed. Please try again.',
        variant: 'error',
      });
    }
  };

  // React state for Atom script management
  const [atomScript, setAtomScript] = useState<HTMLScriptElement | null>(null);

  // React version of handleAtomPayment
  const handleAtomPayment = async (data: any) => {
    try {
      const res = await getAtomScriptUrlService({}).unwrap();
      if (!atomScript) {
        loadAtomScript(res.atomScriptUrl, () => {
          initializeAtomPayment(data);
        });
      } else {
        initializeAtomPayment(data);
      }
    } catch (error: any) {
      console.error('Error processing Atom payment:', error);
    }
  };

  // React version of loadAtomScript
  const loadAtomScript = (scriptUrl: string, callback: () => void): void => {
    const script = document.createElement('script');
    script.src = `${scriptUrl}?v=${Date.now()}`;
    script.async = true;
    script.onload = () => {
      callback();
    };
    script.onerror = () => {
      console.error('Failed to load Atom payment script');
    };
    document.body.appendChild(script);
    setAtomScript(script);
  };

  // React version of initializeAtomPayment
  const initializeAtomPayment = (paymentDetails: any): void => {
    if (window.AtomPaynetz) {
      const options = {
        atomTokenId: paymentDetails.tokenId,
        merchId: paymentDetails.merchantId,
        custEmail: paymentDetails.custEmail,
        custMobile: paymentDetails.custMobile,
        returnUrl: paymentDetails.returnUrl,
      };

      new window.AtomPaynetz(options, 'uat');
    } else {
      console.error('Atom payment script not loaded');
    }
  };

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      if (atomScript) {
        document.body.removeChild(atomScript);
      }
    };
  }, [atomScript]);

  const formatBookingDetails = (data: any) => {
    if (data && data?.length > 0) {
      // Format seats
      const seats = data
        .map((item: any) => item?.seat?.seatNo)
        .filter((seat: any) => seat)
        .join(', ');

      // Format meals
      const meals = data
        .map((item: any) => item?.meal?.desc)
        .filter((meal: any) => meal)
        .join(', ');

      // Format baggage
      const baggages = data
        .map((item: any) => item?.baggage?.desc)
        .filter((baggage: any) => baggage)
        .join(', ');

      return {
        seat: seats.replace(/\s+/g, ' ').replace(/,$/, ''),
        meal: meals.replace(/\s+/g, ' ').replace(/,$/, ''),
        baggage: baggages.replace(/\s+/g, ' ').replace(/,$/, ''),
      };
    }
  };

  const getPaxType = (paxType: string) => {
    if (paxType === 'ADT') return 'Adult';
    if (paxType === 'CHD') return 'Child';
    if (paxType === 'INF') return 'Infant';
  };
  return (
    <div className="bg-tertiary">
      <div className="h-14 w-full bg-primary sm:h-20 lg:h-48" />
      <div className="absolute inset-x-0 top-14 mx-auto px-1 pb-4 sm:top-14 sm:w-[1200px] sm:pb-8 lg:top-32 lg:pb-20">
        <div className="mb-3 flex items-center gap-2 sm:mb-5 lg:mb-7">
          <h2 className="pl-2 text-[10px] font-semibold text-white sm:pl-4 sm:text-lg lg:pl-8 lg:text-xl">
            Review Booking
          </h2>
        </div>
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="w-full rounded-lg bg-white p-3 shadow-lg sm:rounded-xl sm:p-4 lg:basis-[75%] lg:p-6">
            {/**Outboud flight */}
            {selectedFlight?.flightInfo.map((flight: any, index: number) => (
              <div key={index}>
                <div className="rounded-lg bg-background p-3 sm:p-4 lg:px-6 lg:py-4">
                  <span className="mb-2 flex items-center gap-1.5 text-[10px] sm:mb-3 sm:text-sm">
                    {index == 0 ? (
                      <FlightUpIcon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
                    ) : (
                      <FlightDownIcon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
                    )}{' '}
                    {index == 0 ? 'Departing Flight' : 'Return Flight'}
                  </span>
                  <h3 className="line-clamp-1 text-[10px] font-semibold text-zinc-800 sm:text-xl lg:text-2xl">
                    {flight?.departure_city_name} - {flight?.arrival_city_name}
                  </h3>
                  <div className="mt-1 text-[10px] text-zinc-800 sm:text-xs lg:text-sm">
                    <span className="inline-flex items-center gap-1.5">
                      {flight?.airline_code}
                      <span className="h-1 w-1 rounded-full bg-zinc-800"></span>
                      {flight?.stops}
                      <span className="h-1 w-1 rounded-full bg-zinc-800"></span>
                      {flightSearch?.passengers?.cabin}
                    </span>
                  </div>
                </div>
                <FlightDetailComponent
                  data={selectedFlight}
                  tripIndex={index}
                />
              </div>
            ))}
            {/* *Return flight */}
            {flightSearch.travelType === 'RT' &&
              flightSearch.travelMode == 'DO' &&
              isSelectedReturnFlight &&
              Object.keys(isSelectedReturnFlight)?.length && (
                <div>
                  <div className="rounded-lg bg-background p-3 sm:p-4 lg:px-6 lg:py-4">
                    <span className="mb-2 flex items-center gap-1.5 text-[10px] sm:mb-3 sm:text-sm">
                      <FlightDownIcon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
                      Return Flight
                    </span>
                    <h3 className="line-clamp-1 text-[10px] font-semibold text-zinc-800 sm:text-xl lg:text-2xl">
                      {
                        isSelectedReturnFlight?.flightInfo?.[0]
                          ?.departure_city_name
                      }{' '}
                      -{' '}
                      {
                        isSelectedReturnFlight?.flightInfo?.[0]
                          ?.arrival_city_name
                      }
                    </h3>
                    <div className="mt-1 text-[10px] text-zinc-800 sm:text-xs lg:text-sm">
                      <span className="inline-flex items-center gap-1.5">
                        {isSelectedReturnFlight?.airline_code}
                        <span className="h-1 w-1 rounded-full bg-zinc-800"></span>
                        {isSelectedReturnFlight?.duration_stops}
                        <span className="h-1 w-1 rounded-full bg-zinc-800"></span>
                        {flightSearch?.passengers?.cabin}
                      </span>
                    </div>
                  </div>

                  <FlightDetailComponent
                    data={isSelectedReturnFlight}
                    tripIndex={0}
                  />
                </div>
              )}
            {/* Tabs above the bordered box */}
            <Tabs
              defaultValue={`${selectedFlight?.flightInfo[0]?.origin}-${selectedFlight?.flightInfo[0]?.destination}-0`}
              className="mb-4 w-full"
            >
              <TabsList className="flex w-max justify-start gap-x-2 p-0 sm:gap-x-3">
                {[
                  ...(selectedFlight?.flightInfo || []),
                  ...(flightSearch?.travelType === 'RT'
                    ? isSelectedReturnFlight?.flightInfo || []
                    : []),
                ].map((sector: any, index: number) => (
                  <TabsTrigger
                    key={`${sector?.origin}-${sector?.destination}-${index}`}
                    value={`${sector?.origin}-${sector?.destination}-${index}`}
                    className="relative whitespace-nowrap rounded-lg border-b-2 border-transparent bg-transparent px-2 py-1.5 text-[10px] font-semibold text-primary transition-all data-[state=active]:border-primary data-[state=active]:text-primary sm:px-3 sm:py-2 sm:text-base lg:px-4 lg:text-lg"
                  >
                    {sector?.origin + '-' + sector?.destination}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Bordered box for traveller details */}
              <div className="border-light mt-2 rounded-md border border-zinc-300 px-3 pb-4 sm:mt-4 sm:px-4 sm:pb-6 lg:mt-6 lg:px-6 lg:pb-8">
                <h2 className="my-3 text-[10px] font-semibold text-zinc-800 sm:text-xl lg:text-2xl">
                  Travellers Details
                </h2>

                {[
                  ...(selectedFlight?.flightInfo || []),
                  ...(isSelectedReturnFlight?.flightInfo || []),
                ].map((sector: any, sectorIndex: number) => (
                  <TabsContent
                    key={`${sector?.origin}-${sector?.destination}-${sectorIndex}`}
                    value={`${sector?.origin}-${sector?.destination}-${sectorIndex}`}
                  >
                    {flightSearch.passenger.map((data: any, index: number) => (
                      <div
                        className="mt-4"
                        key={index}
                      >
                        {/* === Travel Details + Passport Details side by side === */}
                        <div className={cn('mt-3 grid grid-cols-1 gap-3', flightSearch?.tripType === 'OW' ? 'sm:grid-cols-1' : 'sm:grid-cols-2')}>
                          {/* === Travel Details === */}
                          <div className="py-3">
                            {/* <h3 className="mb-2 text-[10px] font-semibold text-primary sm:text-sm">
                              Travel Details
                            </h3> */}

                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                              <div>
                                <span className="text-[10px] font-bold sm:text-base">
                                  {data?.title} {data?.firstName}{' '}
                                  {data?.lastName}
                                </span>
                                <span className="ml-2 text-[10px] text-zinc-700 sm:text-sm">
                                  ({getPaxType(data?.pax_type)})
                                </span>
                              </div>
                            </div>

                            {data?.dob && (
                              <div className="mb-1 flex text-[10px] sm:text-xs lg:text-sm">
                                <span className="w-24 text-zinc-600">DOB</span>{' '}
                                :<span className="ml-1">{data?.dob}</span>
                              </div>
                            )}

                            {data?.idNumber && (
                              <div className="mb-1 flex text-[10px] sm:text-xs lg:text-sm">
                                <span className="w-24 text-zinc-600">
                                  Id Number
                                </span>{' '}
                                :<span className="ml-1">{data?.idNumber}</span>
                              </div>
                            )}

                            {formatBookingDetails(data.ssrInfo[sectorIndex])
                              ?.seat && (
                              <div className="mb-1 flex text-[10px] sm:text-xs lg:text-sm">
                                <span className="w-24 text-zinc-600">Seat</span>{' '}
                                :
                                <span className="ml-1">
                                  {formatBookingDetails(
                                    data.ssrInfo[sectorIndex],
                                  )?.seat || 'NA'}
                                </span>
                              </div>
                            )}

                            {formatBookingDetails(data.ssrInfo[sectorIndex])
                              ?.meal && (
                              <div className="mb-1 flex text-[10px] sm:text-xs lg:text-sm">
                                <span className="w-24 text-zinc-600">Meal</span>{' '}
                                :
                                <span className="ml-1">
                                  {formatBookingDetails(
                                    data.ssrInfo[sectorIndex],
                                  )?.meal || 'NA'}
                                </span>
                              </div>
                            )}

                            {formatBookingDetails(data.ssrInfo[sectorIndex])
                              ?.baggage && (
                              <div className="flex text-[10px] sm:text-xs lg:text-sm">
                                <span className="w-24 text-zinc-600">
                                  Baggage
                                </span>{' '}
                                :
                                <span className="ml-1">
                                  {formatBookingDetails(
                                    data.ssrInfo[sectorIndex],
                                  )?.baggage || 'NA'}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* === Passport Details === */}
                          {(data?.passport_no ||
                            data?.nationality ||
                            data?.expiry_date) && (
                            <div className="rounded-md border p-3">
                              <h3 className="mb-2 text-[10px] font-semibold text-primary sm:text-sm">
                                Passport Details
                              </h3>

                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                  <span className="text-[10px] font-bold sm:text-base">
                                    {' '}
                                    {data?.title} {data?.firstName}{' '}
                                    {data?.lastName}
                                  </span>
                                </div>
                              </div>

                              {data?.nationality && (
                                <div className="mb-1 flex text-[10px] sm:text-xs lg:text-sm">
                                  <span className="w-28 text-zinc-600">
                                    Nationality
                                  </span>{' '}
                                  :
                                  <span className="ml-1">
                                    {data?.nationality}
                                  </span>
                                </div>
                              )}

                              {data?.passport_no && (
                                <div className="mb-1 flex text-[10px] sm:text-xs lg:text-sm">
                                  <span className="w-28 text-zinc-600">
                                    Passport No
                                  </span>{' '}
                                  :
                                  <span className="ml-1">
                                    {data?.passport_no}
                                  </span>
                                </div>
                              )}

                              {data?.expiry_date && (
                                <div className="mb-1 flex text-[10px] sm:text-xs lg:text-sm">
                                  <span className="w-28 text-zinc-600">
                                    Expiry Date
                                  </span>{' '}
                                  :
                                  <span className="ml-1">
                                    {data?.expiry_date}
                                  </span>
                                </div>
                              )}

                              {data?.issued_country && (
                                <div className="flex text-[10px] sm:text-xs lg:text-sm">
                                  <span className="w-28 text-zinc-600">
                                    Issued Country
                                  </span>{' '}
                                  :
                                  <span className="ml-1">
                                    {data?.issued_country}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </TabsContent>
                ))}
              </div>
            </Tabs>

            {flightSearch?.gstDetails &&
              Object.keys(flightSearch?.gstDetails).length > 0 && (
                <div className="border-light mt-6 rounded-md border border-zinc-300 px-3 pb-4 sm:mt-8 sm:px-4 sm:pb-6 lg:mt-10 lg:px-6 lg:pb-8">
                  <h2 className="mb-3 text-[10px] font-semibold text-zinc-800 sm:mb-4 sm:text-xl lg:mb-6 lg:text-2xl">
                    GST Details
                  </h2>

                  <div className="flex items-center gap-2">
                    <span className="min-w-[140px] text-zinc-600">
                      Gst Company Name
                    </span>
                    <span>:</span>
                    <span className="whitespace-normal break-words">
                      {flightSearch?.gst?.register_company_name || 'NA'}
                    </span>
                  </div>

                  <div className="mt-2 flex items-center gap-2">
                    <span className="min-w-[140px] text-zinc-600">
                      Registration Number
                    </span>
                    <span>:</span>
                    <span className="whitespace-normal break-words">
                      {flightSearch?.gst?.register_no || 'NA'}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="min-w-[140px] text-zinc-600">
                      Registered Mobile
                    </span>
                    <span>:</span>
                    <span className="whitespace-normal break-words">
                      {flightSearch?.gst?.register_mobile || 'NA'}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="min-w-[140px] text-zinc-600">
                      Registered Email
                    </span>
                    <span>:</span>
                    <span className="whitespace-normal break-words">
                      {flightSearch?.gst?.register_email || 'NA'}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="min-w-[140px] text-zinc-600">
                      Company Address
                    </span>
                    <span>:</span>
                    <span className="whitespace-normal break-words">
                      {flightSearch?.gst?.register_address || 'NA'}
                    </span>
                  </div>
                </div>
              )}
          </div>

          <div className="flex basis-[25%] flex-col gap-5">
            <div>
              <FareSummary
                callback={callback}
                summary={selectedBucket}
                addOns={{ isShow: true }}
                convenienceFee={{
                  isShow: true,
                  fee:
                    Number(flightSearch?.fareDetails?.management_fee) +
                    Number(flightSearch?.fareDetails?.management_fee_tax),
                }}
              >
                Pay{' '}
                {formatCurrency(
                  Number(flightSearch?.fareDetails?.netfare || 0) +
                    Number(flightSearch?.fareDetails?.fareDetails?.seat || 0) +
                    Number(flightSearch?.fareDetails?.meal || 0) +
                    Number(flightSearch?.fareDetails?.baggage || 0),
                )}
              </FareSummary>
            </div>
            <div>
              <BookingSummary />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FlightDetailComponent = ({
  data,
  tripIndex,
}: {
  data: Root;
  tripIndex: number;
}) => {
  if (!data || !('layoverStopsData' in data)) return null;

  return (
    <>
      {data.layoverStopsData?.map((item, index) => {
        if (item.tripIndex === tripIndex) {
          return (
            <React.Fragment key={index}>
              <StopsData item={item} />
              {data?.layover?.map((layoverStops, i) => (
                <React.Fragment key={i}>
                  {tripIndex < data.layoverStopsData.length - 1 &&
                    item?.destination !== data?.destination &&
                    index === layoverStops?.tripIndex && (
                      <div className="my-2 flex w-full items-center gap-1.5 sm:my-3 sm:gap-2 lg:my-4">
                        <div className="h-[1px] w-full border-t border-dashed border-text_secondary"></div>
                        <div className="whitespace-nowrap rounded-full bg-light px-3 py-1 text-[10px] text-zinc-800 sm:px-4 sm:py-1.5 sm:text-xs lg:px-6 lg:text-sm">
                          <span className="hidden sm:inline">
                            {layoverStops?.layoverTime &&
                            !layoverStops?.layoverTime.includes('NaN')
                              ? layoverStops?.layoverTime
                              : '00'}{' '}
                            Layover in{' '}
                          </span>
                          <span className="sm:hidden">
                            {layoverStops?.layoverTime} in{' '}
                          </span>
                          {layoverStops?.city}
                          <span className="hidden sm:inline">
                            {' '}
                            (Plane Change)
                          </span>
                        </div>
                        <div className="h-[1px] w-full border-t border-dashed border-text_secondary"></div>
                      </div>
                    )}
                </React.Fragment>
              ))}
            </React.Fragment>
          );
        }
        return null;
      })}
    </>
  );
};

const StopsData = ({ item }: { item: any }) => {
  return (
    <div className="mx-3 my-2 sm:mx-4 sm:my-3 lg:mx-6 lg:my-4">
      <div className="mb-2 flex items-center gap-1.5 sm:mb-3 sm:gap-2">
        <Image
          src={getAirlineIconUrl(item?.airline_code)}
          crossOrigin="anonymous"
          alt={item.airline_code ? item.airline_code : ''}
          className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8"
          width={32}
          height={32}
        />
        <h4 className="text-[10px] font-medium text-zinc-800 sm:text-base">
          {item.airline_name} - {item?.flight_number}
        </h4>
      </div>

      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="w-full sm:w-auto">
          <h2 className="text-[10px] font-bold text-zinc-800 sm:text-lg">
            {item?.departure_city_name}
            <span className="text-[10px] sm:text-base">
              ({item?.origin})
            </span>{' '}
            {item?.departure_time}
          </h2>
          <div className="max-w-[280px] text-[10px] text-zinc-800 sm:max-w-[200px] sm:text-xs lg:max-w-[250px] lg:text-sm">
            {`${item?.departure_airport_name}, ${item?.departure_city_name}, ${item?.departure_country_name}${item?.departure_terminal ? `, ${item?.departure_terminal}` : ''}`}
          </div>
        </div>

        <div className="flex w-full flex-col items-center justify-center gap-0.5 sm:w-auto sm:gap-2">
          <div className="flex items-center">
            <span className="rounded-full border border-gray-400 p-0.5 sm:p-1"></span>
            <span className="relative w-16 border border-dotted border-gray-400 sm:w-20 lg:w-24">
              <Clock className="absolute -top-2 left-6 h-4 w-4 rounded-full bg-white text-orange-400 sm:-top-3 sm:left-7 sm:h-5 sm:w-5 lg:left-9 lg:h-6 lg:w-6" />
            </span>
            <span className="rounded-full border border-gray-400 p-0.5 sm:p-1"></span>
          </div>
          <div className="text-[10px] sm:text-xs lg:text-sm">
            {item?.duration}
          </div>
        </div>

        <div className="w-full sm:w-auto">
          <h2 className="text-[10px] font-bold text-zinc-800 sm:text-lg">
            {item?.arrival_city_name}
            <span className="text-[10px] sm:text-base">
              ({item?.destination})
            </span>{' '}
            {item?.arrival_time}
          </h2>
          <div className="max-w-[280px] text-[10px] text-zinc-800 sm:max-w-[200px] sm:text-xs lg:max-w-[250px] lg:text-sm">
            {`${item?.arrival_airport_name}, ${item?.arrival_city_name}, ${item?.arrival_country_name}${item?.arrival_terminal ? `, ${item?.arrival_terminal}` : ''}`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Review;
